import { useMemo } from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityFilter,
  EntityLifecycleFilter,
  EntityNamespaceFilter,
  EntityOrphanFilter,
  EntityOwnerFilter,
  EntityTagFilter,
  EntityTextFilter,
  EntityUserFilter,
  UserListFilter,
} from '@backstage/plugin-catalog-react';

import { ALLOWED_DAAS_TYPES, DaaSType } from '../types';

/**
 * The filters `EntityListProvider` applies in the browser rather than sending
 * to the catalog when it is not paginating, as the DaaS explorer is not. The
 * counts split them the same way, so the selected tab's count matches the
 * table. Mirrors `reduceBackendCatalogFilters` in `@backstage/plugin-catalog-react`,
 * which is not exported.
 */
const BROWSER_SIDE_FILTERS = [
  EntityTagFilter,
  EntityOwnerFilter,
  EntityLifecycleFilter,
  EntityNamespaceFilter,
  EntityUserFilter,
  EntityOrphanFilter,
  EntityTextFilter,
  UserListFilter,
];

/** What the browser-side filters read, and what the counts group by. */
const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.tags',
  'spec.type',
  'spec.lifecycle',
  'spec.projectName',
  'relations',
];

const isBrowserSide = (filter: EntityFilter) =>
  BROWSER_SIDE_FILTERS.some(type => filter instanceof type);

/**
 * Counts the DaaS catalog Components of each `spec.type` under the explorer's
 * current filters, ignoring the type tab itself so every tab keeps its own
 * number.
 *
 * One request loads a slim list of all three types with the server-side
 * filters applied; the browser-side ones ("starred", "owned by me", the owner
 * picker) run over that list, so changing them recounts without a request.
 *
 * @param filters - the explorer's `useEntityList().filters`
 * @returns `counts` - per type, `undefined` while first loading or after a
 *   failure - and the request's `error`.
 */
export const useDaaSTypeCounts = (
  filters: Record<string, EntityFilter | undefined>,
): {
  counts: Record<DaaSType, number> | undefined;
  error: Error | undefined;
} => {
  const catalogApi = useApi(catalogApiRef);

  const active = useMemo(
    () =>
      Object.entries(filters)
        .filter(([key, filter]) => key !== 'type' && filter)
        .map(([, filter]) => filter as EntityFilter),
    [filters],
  );

  // The filter objects change identity on every update, so the request is
  // keyed on what they send to the catalog: the parsed filter only gets a new
  // identity when that content changes.
  const catalogFilterKey = useMemo(() => {
    const merged: Record<string, string | string[]> = { kind: 'component' };
    for (const filter of active) {
      if (!isBrowserSide(filter)) {
        Object.assign(merged, filter.getCatalogFilters?.() ?? {});
      }
    }
    merged['spec.type'] = [...ALLOWED_DAAS_TYPES];
    return JSON.stringify(merged);
  }, [active]);
  const catalogFilter = useMemo(
    () => JSON.parse(catalogFilterKey) as Record<string, string | string[]>,
    [catalogFilterKey],
  );

  const { value: entities, error } = useAsync(
    async () =>
      (await catalogApi.getEntities({ filter: catalogFilter, fields: FIELDS }))
        .items,
    [catalogApi, catalogFilter],
  );

  const counts = useMemo(() => {
    if (!entities) return undefined;
    const tally = Object.fromEntries(
      ALLOWED_DAAS_TYPES.map(type => [type, 0]),
    ) as Record<DaaSType, number>;
    for (const entity of entities) {
      const type = entity.spec?.type as DaaSType;
      if (
        type in tally &&
        active.every(
          filter => !filter.filterEntity || filter.filterEntity(entity),
        )
      ) {
        tally[type] += 1;
      }
    }
    return tally;
  }, [entities, active]);

  return { counts: error ? undefined : counts, error };
};
